import { computed, Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core'
import { CategoryService } from '@app/core/auth/services/category.service'
import { CertificateCategory } from '@app/shared/models/certificate-category.model'

@Directive({
  selector: '[albertIfCategory]',
  standalone: true
})
/**
 * Structural directive for conditional rendering based on the active certificate category.
 *
 * Usage Examples:
 * 1. Pass a single category:
 *    <div *albertIfCategory="CertificateCategory.LEASE">...</div>
 *
 * 2. Pass an array of categories:
 *    <div *albertIfCategory="[CertificateCategory.LEASE, CertificateCategory.AUTHORITIES]">...</div>
 *
 * 3. Use a configuration object for declarative visibility:
 *    <div *albertIfCategory="'aircraftSerialNumber'; config: HULL_PROVISIONS_VISIBILITY">...</div>
 */
export class IfCategoryDirective {
  /**
   * The category, array of categories, or field name to check against.
   */
  public readonly albertIfCategory = input.required<CertificateCategory | CertificateCategory[] | string>()
  /**
   * Optional configuration object to look up the allowed categories for a field name.
   */
  public readonly albertIfCategoryConfig = input<Record<string, CertificateCategory[] | undefined>>()
  private readonly categoryService = inject(CategoryService)
  private readonly templateRef = inject(TemplateRef<unknown>)
  private readonly viewContainerRef = inject(ViewContainerRef)
  private readonly isVisible = computed(() => {
    const active = this.categoryService.activeCertificateCategory()
    if (active === null) return false

    const value = this.albertIfCategory()
    const config = this.albertIfCategoryConfig()

    let allowed: CertificateCategory[]

    if (typeof value === 'string') {
      if (!config) {
        console.warn('albertIfCategory: Field name provided but no config object supplied.')
        return false
      }
      allowed = config[value] ?? []
    } else {
      allowed = Array.isArray(value) ? value : [value]
    }

    return allowed.includes(active)
  })

  constructor() {
    effect(() => {
      if (this.isVisible()) {
        if (this.viewContainerRef.length === 0) {
          this.viewContainerRef.createEmbeddedView(this.templateRef)
        }
      } else {
        this.viewContainerRef.clear()
      }
    })
  }
}
